import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type MediaAsset, type PublishedReel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MEDIA_THUMB_CLASS, MEDIA_THUMB_FRAME, MEDIA_THUMB_LIMIT_STYLE, MediaThumb } from "@/components/MediaThumb";
import { cn } from "@/lib/utils";

function fmt(sec: number) {
  const n = Math.max(0, Number(sec) || 0);
  const m = Math.floor(n / 60);
  const s = (n - m * 60).toFixed(1);
  return m ? `${m}:${s.padStart(4, "0")}` : `${Number(s).toFixed(1)}s`;
}

export default function ReelClipModal({
  carouselId,
  indexN,
  apply = "one",
  fallback = false,
  onDone,
  onClose,
}: {
  carouselId: string;
  indexN: number;
  apply?: "one" | "all";
  fallback?: boolean;
  onDone: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [tab, setTab] = useState<"library" | "graph">("library");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<MediaAsset | null>(null);
  const [inSec, setInSec] = useState(0);
  const [outSec, setOutSec] = useState(3);
  const [scale, setScale] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const listQ = useQuery({
    queryKey: ["media-reels", q],
    queryFn: () => api.mediaList({ kind: "video", q }),
  });
  const graphQ = useQuery({
    queryKey: ["media-reels-published"],
    queryFn: () => api.mediaReelsPublished(),
    enabled: tab === "graph",
  });
  const duration = picked?.duration_sec || 0;
  const span = Math.max(0, outSec - inSec);
  const canVideo = span + 0.001 >= 3;
  const ffmpeg = listQ.data?.ffmpeg !== false;

  const pickAsset = (asset: MediaAsset) => {
    setPicked(asset);
    const dur = Number(asset.duration_sec || 0);
    setInSec(0);
    setOutSec(dur >= 3 ? Math.min(dur, 5) : Math.max(dur, 0.2));
  };

  const uploadM = useMutation({
    mutationFn: (file: File) => api.mediaUpload(file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["media-reels"] });
      if (res.asset) pickAsset(res.asset);
    },
  });
  const importM = useMutation({
    mutationFn: (id: string) => api.mediaReelImport(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["media-reels"] });
      if (res.asset) {
        setTab("library");
        pickAsset(res.asset);
      }
    },
  });
  const clipM = useMutation({
    mutationFn: (mode: "still" | "video") => {
      if (!picked) throw new Error("wybierz rolkę");
      return api.mediaClip(picked.id, {
        in_sec: inSec,
        out_sec: outSec,
        mode,
        carousel_id: carouselId,
        index_n: indexN,
        apply,
        fallback,
        scale,
        x: panX,
        y: panY,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel", carouselId] });
      onDone();
    },
  });

  const seek = (t: number) => {
    const el = videoRef.current;
    if (el) el.currentTime = t;
  };

  const published: PublishedReel[] = graphQ.data?.items || [];
  const reels = useMemo(() => listQ.data?.items || [], [listQ.data]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-sm font-medium">Fragment rolki jako tło</h2>
            <p className="text-[11px] text-muted-foreground">
              Wgraj MP4 albo pobierz własną rolkę z Graph, gdy da plik. Cięcie jest na serwerze.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={onClose}>
            Zamknij
          </Button>
        </div>
        <div className="flex gap-2 border-b px-4 py-2">
          <button
            type="button"
            className={cn("rounded border px-2 py-0.5 text-[11px]", tab === "library" ? "border-brand bg-brand/10" : "border-border")}
            onClick={() => setTab("library")}
          >
            Biblioteka
          </button>
          <button
            type="button"
            className={cn("rounded border px-2 py-0.5 text-[11px]", tab === "graph" ? "border-brand bg-brand/10" : "border-border")}
            onClick={() => setTab("graph")}
          >
            Opublikowane IG
          </button>
          <label className="ml-auto inline-flex h-7 cursor-pointer items-center rounded-md border border-input px-2 text-[11px]">
            Wgraj MP4
            <input
              type="file"
              accept="video/mp4,video/quicktime,.mp4,.mov"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadM.mutate(file);
              }}
            />
          </label>
        </div>
        {!ffmpeg ? (
          <p className="px-4 pt-2 text-xs text-destructive">
            Na serwerze nie ma ffmpeg. Upload zadziała, cięcie nie. Doinstaluj ffmpeg.
          </p>
        ) : null}
        <div className="grid min-h-0 flex-1 gap-3 overflow-auto p-4 md:grid-cols-[1fr_1fr]">
          <div className="space-y-2">
            {tab === "library" ? (
              <>
                <input
                  className="w-full rounded border px-2 py-1 text-xs"
                  placeholder="szukaj rolki"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <div className="grid max-h-72 grid-cols-2 gap-2 overflow-auto">
                  {reels.map((asset) => (
                    <button
                      key={asset.id}
                      type="button"
                      className={cn(
                        "overflow-hidden rounded border text-left",
                        picked?.id === asset.id ? "border-brand" : "border-border",
                      )}
                      onClick={() => pickAsset(asset)}
                    >
                      <MediaThumb asset={asset} />
                      <p className="truncate px-1.5 py-1 text-[11px]">{asset.original_name || asset.id.slice(0, 8)}</p>
                      <p className="px-1.5 pb-1 text-[10px] text-muted-foreground">
                        {asset.duration_sec ? fmt(asset.duration_sec) : "wideo"}
                        {asset.origin === "graph_reel" ? " · Graph" : ""}
                      </p>
                    </button>
                  ))}
                </div>
                {!reels.length ? (
                  <p className="text-xs text-muted-foreground">Pusto. Wgraj MP4 albo wrzuć pliki do inbox/rolki.</p>
                ) : null}
              </>
            ) : (
              <>
                {graphQ.data?.error ? <p className="text-xs text-destructive">{graphQ.data.error}</p> : null}
                <div className="grid max-h-72 grid-cols-2 gap-2 overflow-auto">
                  {published.map((row) => (
                    <div key={row.id} className="overflow-hidden rounded border">
                      {row.thumbnail_url ? (
                        <span className={MEDIA_THUMB_FRAME} style={MEDIA_THUMB_LIMIT_STYLE}>
                          <img
                            src={row.thumbnail_url}
                            alt={row.caption || "Miniatura rolki"}
                            className={MEDIA_THUMB_CLASS}
                            style={{ ...MEDIA_THUMB_LIMIT_STYLE, width: "auto", height: "auto", objectFit: "contain" }}
                          />
                        </span>
                      ) : (
                        <div
                          className={cn(MEDIA_THUMB_FRAME, "text-[11px] text-muted-foreground")}
                          style={MEDIA_THUMB_LIMIT_STYLE}
                        >
                          brak miniatury
                        </div>
                      )}
                      <p className="truncate px-1.5 py-1 text-[11px]">{row.caption || row.id}</p>
                      {row.downloadable ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="m-1"
                          disabled={importM.isPending}
                          onClick={() => importM.mutate(row.id)}
                        >
                          Pobierz na serwer
                        </Button>
                      ) : (
                        <p className="px-1.5 pb-1 text-[10px] text-muted-foreground">{row.hint}</p>
                      )}
                    </div>
                  ))}
                </div>
                {importM.error ? (
                  <p className="text-xs text-destructive">
                    {importM.error instanceof Error ? importM.error.message : "import nie wyszedł"}
                  </p>
                ) : null}
              </>
            )}
          </div>
          <div className="space-y-2">
            {picked ? (
              <>
                <video
                  ref={videoRef}
                  src={api.mediaFile(picked.id, false)}
                  className="aspect-[4/5] w-full rounded bg-black object-contain"
                  controls
                  muted
                  playsInline
                  onLoadedMetadata={(e) => {
                    const dur = e.currentTarget.duration;
                    if (dur && !picked.duration_sec) setOutSec(dur >= 3 ? Math.min(dur, 5) : dur);
                  }}
                />
                <label className="block text-[11px] text-muted-foreground">
                  Od {fmt(inSec)}
                  <input
                    type="range"
                    min={0}
                    max={Math.max(duration, outSec, 0.1)}
                    step={0.1}
                    className="mt-1 w-full"
                    value={inSec}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setInSec(next);
                      if (next >= outSec) setOutSec(next + 0.2);
                      seek(next);
                    }}
                  />
                </label>
                <label className="block text-[11px] text-muted-foreground">
                  Do {fmt(outSec)}
                  <input
                    type="range"
                    min={0}
                    max={Math.max(duration, outSec, inSec + 0.2)}
                    step={0.1}
                    className="mt-1 w-full"
                    value={outSec}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setOutSec(next);
                      seek(next);
                    }}
                  />
                </label>
                <p className="text-[11px] text-muted-foreground">
                  Fragment: {fmt(span)}. Slajd wideo na IG wymaga min. 3 s.
                </p>
                <label className="block text-[11px] text-muted-foreground">
                  Zbliżenie {scale.toFixed(2)}×
                  <input
                    type="range"
                    min={1}
                    max={2.5}
                    step={0.05}
                    className="mt-1 w-full"
                    value={scale}
                    onChange={(e) => setScale(Number(e.target.value))}
                  />
                </label>
                <label className="block text-[11px] text-muted-foreground">
                  Pozycja X {panX}
                  <input
                    type="range"
                    min={-400}
                    max={400}
                    step={10}
                    className="mt-1 w-full"
                    value={panX}
                    onChange={(e) => setPanX(Number(e.target.value))}
                  />
                </label>
                <label className="block text-[11px] text-muted-foreground">
                  Pozycja Y {panY}
                  <input
                    type="range"
                    min={-400}
                    max={400}
                    step={10}
                    className="mt-1 w-full"
                    value={panY}
                    onChange={(e) => setPanY(Number(e.target.value))}
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="brand" disabled={clipM.isPending || !ffmpeg} onClick={() => clipM.mutate("still")}>
                    Klatka jako tło
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={clipM.isPending || !ffmpeg || !canVideo}
                    onClick={() => clipM.mutate("video")}
                  >
                    Slajd wideo
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Wybierz rolkę z lewej, potem ustaw zakres.</p>
            )}
            {clipM.error ? (
              <p className="text-xs text-destructive">
                {clipM.error instanceof Error ? clipM.error.message : "cięcie nie wyszło"}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
